import { Button, FormControl, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import { Button1 } from 'components/Button';
import CusDataGrid from 'components/CusDataGrid';
import CusDrawer from 'components/CusDrawer';
import CusModal from 'components/CusModal';
import MainCard from 'components/MainCard';
import useMain from 'hooks/useMain';
import { sharedMainGridColumns, userAdminGridColumns } from 'pages/module/column/table-columns';
import { SidebarForm } from 'pages/module/component';
import { advancedFilterFields } from 'pages/module/field';
import { userEditFields } from 'pages/userAdmin/user-fields';
import React, { useEffect, useMemo, useState } from 'react';
import { micService } from 'services/mic';
import { userAdminService } from 'services/userAdmin';
import { rFetchCompanies, setState } from 'store/reducers/comModule';
import { getProduct, handleAlertMessage, handleAsyncCall } from 'utils/common';
import { UserAdminSidebar } from './sidebar';

const Index = () => {
  const [openSiderbar, setOpenSidebar] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchError, setSearchError] = useState('');
  const { t, useNavigate, useLocation, dispatch, useSelector, project, upperCaseLang } = useMain();
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const userProjsRoles = useSelector((s) => s.comModule.userProjsRoles);
  const navigate = useNavigate();
  const isNew = useMemo(() => selectedRow.UserName && !selectedRow.UserId, [selectedRow]);
  useEffect(() => {
    if (!openSiderbar) {
      handleAsyncCall({
        service: userAdminService.getUserList,
        successCb: (res) => {
          setRows(res.data);
        }
      });
    }
  }, [openSiderbar]);

  useEffect(() => {
    // Admin product (ProdName "Admin") hosts UserAdmin + AppAdmin sections
    if (!getProduct()?.ProdName?.match(/admin/i)) {
      navigate('/product');
    }
  }, []);

  const handleEdit = (targetUserId) => {
    const sRow = rows.find(({ UserId }) => UserId === targetUserId);
    setSelectedRow(sRow);
    const _userProjsRoles = sRow.ProjRoles?.map((pr) => ({
      ...pr,
      Permission: `${pr.ProjName} ${pr.RoleName}`
    }));

    dispatch(setState(['userProjsRoles', _userProjsRoles]));
    setOpenSidebar(true);
  };

  const validateUserName = () => {
    handleAsyncCall({
      service: userAdminService.validateByUserName,
      params: searchValue,
      successCb: (res) => {
        if (res.data.Id) {
          setSearchError(t('ERR_USER_EXIST'));
          return;
        }
        setOpenModal(false);
        setSearchValue('');
        setOpenSidebar(true);
        setSelectedRow({
          UserName: searchValue
        });
      }
    });
  };

  const postProjRoles = async (newUserData) => {
    await handleAsyncCall({
      service: userAdminService.postProjRoles,
      params: {
        UserId: selectedRow?.UserId || newUserData?.Id,
        ProjUserRole: userProjsRoles.map((props) => ({ ...props, UserId: selectedRow?.UserId || newUserData?.Id }))
      },

      successCb: () => {
        handleAlertMessage({
          text: t('SUCCESS_SUBMIT'),
          icon: 'success',
          showCancelButton: false,
          cb: (result) => {
            setOpenSidebar(false);
            dispatch(setState(['userProjsRoles', []]));
          }
        });
      },
      cusExceptionHandle: (response) => {
        handleAlertMessage({
          text: t(response.data?.Message),
          icon: 'error',
          showCancelButton: false
        });
      }
    });
  };

  const handleSubmit = async (data) => {
    if (!_.isEmpty(userProjsRoles)) {
      handleAsyncCall({
        service: isNew ? userAdminService.createUserDetail : userAdminService.updateUserDetail,
        params: { ...data, ProjRoles: userProjsRoles },
        successCb: async (res) => {
          const newUserData = { ...res.data, UserId: res.data?.Id };
          setSelectedRow(newUserData);
          await postProjRoles(newUserData);

          dispatch(setState(['userProjsRoles', []]));
        },
        cusExceptionHandle: (response) => {
          const splitErr = response?.data?.Message?.split('; ') || [response?.data?.Message];

          handleAlertMessage({
            html: (
              <>
                {splitErr.map((err) => (
                  <Typography key={err}>{t(err)}</Typography>
                ))}
              </>
            ),
            icon: 'error',
            showCancelButton: false
          });
        }
      });
    } else {
      handleAlertMessage({
        text: t('ASSIGN_ONE_ROLE'),
        showCancelButton: false
      });
    }
  };

  // const showAuditLogs = () => {
  //   handleAsyncCall({
  //     service: micService.getHistory,
  //     params: {
  //       id: selectedRow.UserId,
  //       stepCode:
  //     },
  //     successCb: (res) => {
  //       setOpenSidebar(false);
  //     }
  //   });
  // };

  return (
    <MainCard>
      {/* <Box height={'500px'} width="100%"> */}
      <CusDataGrid
        showExport={false}
        cusToolbarComponents={() => (
          <>
            <Button1
              text={t('BTN_ADD')}
              sx={{ ml: [0, 2], mt: [2, 'auto'] }}
              variant="contained"
              onClick={() => {
                setSelectedRow({});
                setOpenModal(!openModal);
              }}
            />
          </>
        )}
        disableSelectionOnClick
        getRowId={(row) => row.UserId}
        rows={rows}
        columns={userAdminGridColumns({ openSiderbar, t, upperCaseLang, handleEdit })}
      />
      <UserAdminSidebar openSiderbar={openSiderbar} setOpenSidebar={setOpenSidebar} handleSubmit={handleSubmit} selectedRow={selectedRow} />
      <CusModal
        title={t('VALIDATE_BY_USERNAME')}
        open={openModal}
        onClose={() => {
          setSearchError('');
          setSearchValue('');
        }}
        setOpen={setOpenModal}
        content={
          <Stack spacing={2}>
            <FormControl error={searchError}>
              <TextField
                fullWidth
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
              {searchError && <FormHelperText id="component-error-text">{searchError}</FormHelperText>}
            </FormControl>

            <Button onClick={validateUserName}>{t('SEARCH')}</Button>
          </Stack>
        }
      />
    </MainCard>
  );
};

export default Index;
